import React, { useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAsyncData } from "@/hooks/useAsyncData";
import { LoadErrorState, LoadingState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Download, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { CountsTable, rangeText, dateParam, rangeProblem } from "./CountsTable";

const monthStart = (today) => (today ? `${today.slice(0, 7)}-01` : "");

export default function BackupTab() {
  const { today } = useApp();
  const [from, setFrom] = useState(monthStart(today));
  const [to, setTo] = useState(today);
  const [busy, setBusy] = useState(false);
  const problem = rangeProblem(from, to);
  const qFrom = problem ? undefined : dateParam(from);
  const qTo = problem ? undefined : dateParam(to);

  const stats = useAsyncData(
    () => api.get("/backup/stats", { params: { date_from: qFrom, date_to: qTo } }).then((r) => r.data),
    [qFrom, qTo],
    { keepPrevious: true }
  );

  const download = async () => {
    if (!from || !to || problem) { toast.error(problem || "Choose both dates"); return; }
    setBusy(true);
    try {
      const res = await api.post("/backup/export", { date_from: from, date_to: to }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `rokadly-backup_${from}_${to}.rkb`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Encrypted backup downloaded");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="backup-tab">
      <Card className="rounded-lg"><CardContent className="p-4 space-y-4">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded bg-[hsl(var(--emerald))]/15 flex items-center justify-center shrink-0"><ShieldCheck className="h-4 w-4 text-[hsl(var(--emerald))]" /></div>
          <div>
            <h2 className="text-base md:text-lg font-semibold">Encrypted backup of a date range</h2>
            <p className="text-sm text-muted-foreground">The file is AES-256 encrypted with this Rokadly installation's key — it can only be opened by uploading it back into Rokadly (Restore tab). Users, stores, banks and heads are included so a wiped system can be rebuilt.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5"><Label>From</Label><Input type="date" value={from} max={to || today} onChange={(e) => setFrom(e.target.value)} className="h-10 w-[165px]" data-testid="backup-from" /></div>
          <div className="space-y-1.5"><Label>To</Label><Input type="date" value={to} min={from} max={today} onChange={(e) => setTo(e.target.value)} className="h-10 w-[165px]" data-testid="backup-to" /></div>
          <Button onClick={download} disabled={busy || !from || !to || !!problem} className="h-10 bg-primary hover:bg-[hsl(var(--sapphire-2))]" data-testid="backup-download-button">
            <Download className="h-3.5 w-3.5 mr-1.5" />{busy ? "Preparing…" : "Download encrypted backup"}
          </Button>
          {problem && <span className="text-xs text-[hsl(var(--warning))] pb-2.5" data-testid="backup-range-problem">{problem}</span>}
        </div>
      </CardContent></Card>

      {stats.error && !stats.data ? <LoadErrorState error={stats.error} onRetry={stats.reload} title="Could not count records" />
        : !stats.data ? <LoadingState />
        : (
          <CountsTable counts={stats.data.counts} labels={stats.data.dated} rangeLabel={rangeText(from, to)} testId="backup-counts" />
        )}
    </div>
  );
}
